import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import customFetch from "hooks/fetchWrapper";
import { toast } from "react-toastify";

const StudentDetailsTab = ({ parentEmail, onStudentAdded }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openAddModal, setOpenAddModal] = useState(false);

  const emailToUse = parentEmail || localStorage.getItem("userEmail") || "";

  const initialFormState = {
    Session: "",
    RollNo: "",
    MemFName: "",
    classname: "",
    section: "",
    memsex: "Male",
    MemDOB: "",
    FathFName: "",
    MothFName: "",
    FPhoneM: "",
    MPhoneM: "",
    Email: emailToUse,
  };

  const [formData, setFormData] = useState(initialFormState);

  const fetchParentStudents = async () => {
    if (!emailToUse) return;
    setLoading(true);
    try {
      const response = await customFetch(`students/email/${encodeURIComponent(emailToUse)}`);
      if (response && response.status && Array.isArray(response.data)) {
        setStudents(response.data);
      } else if (Array.isArray(response)) {
        setStudents(response);
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error("Error fetching students by email:", error);
      try {
        const allStudents = await customFetch("students");
        if (Array.isArray(allStudents)) {
          const matched = allStudents.filter(
            (s) => s.Email && s.Email.toLowerCase() === emailToUse.toLowerCase()
          );
          setStudents(matched);
        }
      } catch (err) {
        console.error("Fallback error:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParentStudents();
  }, [emailToUse]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "Session" || name === "classname") {
      newValue = value.replace(/[^0-9]/g, "");
    }
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.Session || !formData.RollNo || !formData.MemFName || !formData.classname) {
      toast.error("Please fill in all required fields (Academic Year, Admission No, Student Name, Grade).");
      return;
    }

    try {
      const userId = localStorage.getItem("userId") || null;
      const payload = {
        ...formData,
        Email: emailToUse,
        parent_id: userId,
        parentId: userId,
        PupilId: formData.RollNo,
        isActive: true,
      };

      const response = await customFetch("students", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (response && (response.status || response.RollNo)) {
        toast.success("Student details added successfully!");
        setOpenAddModal(false);
        setFormData({ ...initialFormState, Email: emailToUse });
        localStorage.setItem("hasStudents", "true");
        fetchParentStudents();
        if (onStudentAdded) onStudentAdded();
      } else {
        toast.error(response.message || "Failed to add student.");
      }
    } catch (error) {
      console.error("Error adding student:", error);
      toast.error(error.message || "Failed to add student. Please check details.");
    }
  };

  // Flex width column definitions to fit screen without horizontal scroll
  const columns = [
    { field: "Session", headerName: "Academic Year", flex: 0.9, minWidth: 95 },
    { field: "RollNo", headerName: "Admission No.", flex: 0.9, minWidth: 100 },
    { field: "MemFName", headerName: "Student Name", flex: 1.2, minWidth: 130 },
    { field: "classname", headerName: "Grade", flex: 0.6, minWidth: 65 },
    { field: "section", headerName: "Division", flex: 0.6, minWidth: 60 },
    { field: "memsex", headerName: "Gender", flex: 0.6, minWidth: 60 },
    {
      field: "MemDOB",
      headerName: "Date of Birth",
      flex: 0.9,
      minWidth: 95,
      valueGetter: (params) => {
        const val = params.row.MemDOB;
        if (!val) return "-";
        return new Date(val).toLocaleDateString();
      },
    },
    { field: "FathFName", headerName: "Father Name", flex: 1.1, minWidth: 110 },
    { field: "FPhoneM", headerName: "Father Contact", flex: 1.0, minWidth: 100 },
  ];

  const rows = students.map((item, index) => ({
    id: item.RollNo || `student-${index}`,
    ...item,
  }));

  return (
    <Box sx={{ py: 0.5, width: "100%" }}>
      {/* Header Bar */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justify: "space-between",
          mb: 2.5,
          width: "100%",
        }}
      >
        <Typography variant="h5" fontWeight="bold" color="primary">
          Student Details & Registration
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddModal(true)}
          sx={{ ml: "auto", px: 2.5, py: 0.8, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          Add Child / Student
        </Button>
      </Box>

      {/* DataGrid Table formatted to fit full width without horizontal scrolling */}
      <Paper elevation={0} sx={{ height: 480, width: "100%", border: "1px solid #e2e8f0", borderRadius: 0 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pageSize={10}
          rowsPerPageOptions={[5, 10, 25]}
          disableSelectionOnClick
          sx={{
            width: "100%",
            borderRadius: 0,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f8fafc",
              fontWeight: "bold",
            },
            "& .MuiDataGrid-cell": {
              fontSize: "0.875rem",
            },
          }}
        />
      </Paper>

      {/* Add Student Dialog Form */}
      <Dialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", borderBottom: "1px solid #e0e0e0" }}>
          Add Child / Student Information
        </DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="School Academic Year"
                  name="Session"
                  value={formData.Session}
                  onChange={handleChange}
                  required
                  placeholder="e.g. 2025"
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Admission No."
                  name="RollNo"
                  value={formData.RollNo}
                  onChange={handleChange}
                  required
                  placeholder="e.g. ADM-1029"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name of the Student"
                  name="MemFName"
                  value={formData.MemFName}
                  onChange={handleChange}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Grade"
                  name="classname"
                  value={formData.classname}
                  onChange={handleChange}
                  required
                  placeholder="e.g. 5"
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Division / Section"
                  name="section"
                  value={formData.section}
                  onChange={handleChange}
                  required
                  placeholder="e.g. A"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Gender</InputLabel>
                  <Select
                    name="memsex"
                    value={formData.memsex}
                    label="Gender"
                    onChange={handleChange}
                  >
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date of Birth"
                  name="MemDOB"
                  InputLabelProps={{ shrink: true }}
                  value={formData.MemDOB}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Father Name"
                  name="FathFName"
                  value={formData.FathFName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mother Name"
                  name="MothFName"
                  value={formData.MothFName}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Father Contact No."
                  name="FPhoneM"
                  value={formData.FPhoneM}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mother Contact No."
                  name="MPhoneM"
                  value={formData.MPhoneM}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenAddModal(false)} color="inherit">
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" sx={{ px: 4 }}>
              Save Student
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default StudentDetailsTab;
